import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  BookOpen,
  HelpCircle,
  Users,
  Video,
  FileText,
  Download,
  ArrowRight,
  ExternalLink,
  PlayCircle,
  Lightbulb,
  GraduationCap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const ResourcesPage = () => {
  const resourceCategories = [
    {
      title: 'Blog',
      description: 'Latest insights, tips, and trends in social media marketing',
      icon: BookOpen,
      link: '/blog',
      color: 'emerald',
      featured: true
    },
    {
      title: 'Help Center',
      description: 'Find answers to common questions and learn how to use StelliSocial',
      icon: HelpCircle,
      link: '/help',
      color: 'blue'
    },
    {
      title: 'Community',
      description: 'Connect with other marketers and share best practices',
      icon: Users,
      link: '/community',
      color: 'purple'
    },
    {
      title: 'Webinars',
      description: 'Live and on-demand sessions with industry experts',
      icon: Video,
      link: '/webinars',
      color: 'orange'
    }
  ];

  const guides = [
    {
      title: 'Getting Started Guide',
      description: 'Everything you need to know to start using StelliSocial effectively.',
      type: 'Guide',
      readTime: '10 min read'
    },
    {
      title: 'Content Calendar Best Practices',
      description: 'Learn how to plan and organize your social media content like a pro.',
      type: 'Guide',
      readTime: '8 min read'
    },
    {
      title: 'AI Content Generation Tips',
      description: 'Maximize the power of AI to create engaging social media posts.',
      type: 'Tutorial',
      readTime: '12 min read'
    },
    {
      title: 'Analytics Deep Dive',
      description: 'Understanding your metrics and making data-driven decisions.',
      type: 'Guide',
      readTime: '15 min read'
    }
  ];

  const webinars = [
    {
      title: 'Social Media Trends 2025',
      date: 'Live Session',
      speakers: ['Sarah Johnson', 'Mike Chen']
    },
    {
      title: 'Mastering Instagram Reels',
      date: 'On Demand',
      speakers: ['Emma Williams']
    },
    {
      title: 'Building Brand Voice',
      date: 'On Demand',
      speakers: ['David Park']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/resources" className="text-emerald-600 font-medium">Resources</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">Resources</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Learn, Grow, and
            <span className="text-emerald-500"> Succeed</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Explore our comprehensive collection of guides, tutorials, webinars, and community resources 
            to master social media marketing.
          </p>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resourceCategories.map((category, i) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={category.link}>
                  <Card className="h-full border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all group cursor-pointer">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-${category.color}-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <category.icon className={`w-6 h-6 text-${category.color}-600`} />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                        {category.title}
                      </h3>
                      <p className="text-slate-600">{category.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Guides */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Featured Guides</h2>
              <p className="text-slate-600">Step-by-step tutorials to help you get the most out of StelliSocial</p>
            </div>
            <Link to="/help">
              <Button variant="outline">View All Guides <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {guides.map((guide, i) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge variant="secondary">{guide.type}</Badge>
                      <span className="text-sm text-slate-500">{guide.readTime}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{guide.title}</h3>
                    <p className="text-slate-600 mb-4">{guide.description}</p>
                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                      Read Guide <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Webinars */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Webinars</h2>
              <p className="text-slate-600">Learn from industry experts in live and on-demand sessions</p>
            </div>
            <Link to="/webinars">
              <Button variant="outline">View All <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {webinars.map((webinar, i) => (
              <motion.div
                key={webinar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <PlayCircle className="w-5 h-5 text-emerald-500" />
                      <Badge variant={webinar.date === 'Live Session' ? 'default' : 'secondary'}>
                        {webinar.date}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{webinar.title}</h3>
                    <p className="text-sm text-slate-500">
                      Speakers: {webinar.speakers.join(', ')}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <GraduationCap className="w-12 h-12 text-white mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-emerald-100 mb-8">
            Get the latest tips, guides, and product updates delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white"
            />
            <Button className="bg-slate-900 hover:bg-slate-800 text-white px-6">
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
